import React, { useState, useEffect } from "react";
import { Dialog } from "./Dialog";
import { useAuth } from "contexts/AuthProvider";
import { UserAccountResponse } from "components/entities";
import { TabHeader } from "components/common/TabHeader";
import { GeneralTab } from "./settings/GeneralTab";
import { DeleteAccountDialog } from "./settings/account-deletion/DeleteAccountDialog";
import { NotificationsTab } from "./settings/NotificationsTab";
import { AppearanceTab } from "./settings/AppearanceTab";

type Tab = "general" | "notifications" | "appearance";

interface SettingsDialogProps {
  open: boolean;
  onClose: () => void;
  initialTab?: Tab;
  accountDetails: UserAccountResponse | undefined;
  onAccountUpdated: () => void;
}

export const SettingsDialog: React.FC<SettingsDialogProps> = ({
  open,
  onClose,
  initialTab = "general",
  accountDetails,
  onAccountUpdated,
}) => {
  const { email, accountStatus } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>(initialTab);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  useEffect(() => {
    if (open) setActiveTab(initialTab);
  }, [open, initialTab]);

  const handleClose = () => {
    setActiveTab("general");
    setShowDeleteDialog(false);
    onClose();
  };

  const isScheduledForDeletion = accountStatus === "scheduledfordeletion";

  const tabs: { id: Tab; label: string }[] = [
    { id: "general", label: "General" },
    { id: "notifications", label: "Notifications" },
    { id: "appearance", label: "Appearance" },
  ];

  return (
    <>
      <Dialog
        open={open && !showDeleteDialog}
        onClose={handleClose}
        title="Settings"
        autoSpacing={false}
        className="max-w-140 min-w-70"
      >
        <div className="flex flex-col gap-4">
          <TabHeader
            tabs={tabs}
            activeTabId={activeTab}
            onTabChange={(id) => setActiveTab(id as Tab)}
          />

          {activeTab === "general" && (
            <GeneralTab
              accountDetails={accountDetails}
              email={email}
              isScheduledForDeletion={isScheduledForDeletion}
              onDeleteAccount={() => setShowDeleteDialog(true)}
              onClose={handleClose}
              onAccountUpdated={onAccountUpdated}
            />
          )}
          {activeTab === "appearance" && <AppearanceTab />}
          {activeTab === "notifications" && <NotificationsTab />}
        </div>
      </Dialog>

      <DeleteAccountDialog open={showDeleteDialog} onClose={handleClose} />
    </>
  );
};
